import Vue, { PropOptions } from "vue";
import { assert, clone } from "@gemma/utils";

import { Events } from "@timesheets/webapp-support";
import { IComputed } from "./IComputed";
import { IDynamicSearchableQueryOptions } from "@timesheets/webapp-support";
import { IFilter } from "./IFilter";
import { IMethods } from "./IMethods";
import { IProps } from "./IProps";
import { IQueryOptions } from "./IQueryOptions";
import { IQueryOptionsData } from "./IQueryOptionsData";
import { IThisResolveSortingFields } from "./IThisResolveSortingFields";
import { IVue } from "../../app/mixins/IVue";
import { ResolvedQueryOptions } from "@gemma/querying";
import { SortDirection } from "@gemma/linq";
import { TBase } from "@timesheets/constants-common";
import { WebApplicationConstants } from "@/setup/WebApplicationConstants";
import { getComputed } from "./getComputed";
import { getDefaultMergedDynamicQueryOptions } from "./getDefaultMergedDynamicQueryOptions";
import { getMethods } from "./getMethods";
import { icons } from "@/utils";
import { pageSizes } from "./pageSizes";
import { resolveSortingFields } from "./resolveSortingFields";

export function getQueryOptionsMixin<TFilter extends IFilter = IFilter & Record<string, unknown>>() {
  return Vue.extend<IQueryOptionsData<TFilter>, IMethods<TFilter>, IComputed, IProps<TFilter>>({
    props: {
      dynamicQueryOptions: {
        type: Object,
        default: () => getDefaultMergedDynamicQueryOptions<TFilter>({} as IDynamicSearchableQueryOptions<TFilter>),
      } as PropOptions<IDynamicSearchableQueryOptions<TFilter>>,
    },
    data(this: IProps<TFilter>): IQueryOptionsData<TFilter> {
      const queryOptions = {
        //
        groupBy: null,
        filter: {} as TFilter,
        page: { index: 0, size: 50 },
        search: null,
        sort: [],
      } as ResolvedQueryOptions<TFilter>;

      const dynamicQueryOptions = clone(this.dynamicQueryOptions);
      const dynamicQueryOptions_ = getDefaultMergedDynamicQueryOptions<TFilter>(dynamicQueryOptions);

      return {
        dynamicQueryOptions_,
        queryOptions,
        oldQueryOptions: clone(queryOptions),
        // proxyQueryOptions: clone(this.options.queryOptions),
        PageSizes: pageSizes,
        filter: {} as TFilter,
        groupBy: null,
        defaultSort: [],
        page: { index: 0, size: 50 },
        search: null,
        sort: [],
        sorting: {
          current: null,
          fields: {},
          icon: {
            [SortDirection.Forward]: icons.chevronDown,
            [SortDirection.Backward]: icons.chevronUp,
          },
        },
        isQueryOptionsLoading: true,
      };
    },
    watch: {
      dynamicQueryOptions_: {
        handler(this: IQueryOptions<TFilter>, newQueryOptions: IDynamicSearchableQueryOptions<TFilter>, oldQueryOptions?: IDynamicSearchableQueryOptions<TFilter>) {
          this.onDynamicQueryOptionsChange(newQueryOptions, oldQueryOptions);
        },
        deep: true,
      },
      queryOptions: {
        handler(this: IQueryOptions<TFilter>, newQueryOptions: ResolvedQueryOptions<TFilter>, oldQueryOptions: ResolvedQueryOptions<TFilter>) {
          this.onQueryOptionsChange(newQueryOptions, oldQueryOptions);
        },
        deep: true,
      },
    },
    computed: getComputed(),
    async beforeMount(this: IQueryOptions<TFilter> & { resort(this: IQueryOptions<TFilter>, fieldName: string): Promise<unknown[]>; type: TBase & { Behaviour?: TBase } }) {
      if (!this.groupedQueryOptionsId) {
        this.groupedQueryOptionsId = this.type.Behaviour ? this.type.Behaviour.CollectionNameUpper : this.type.CollectionNameUpper;
      }

      assert.ok(this.groupedQueryOptionsId, "groupedQueryOptionsId");

      await resolveSortingFields.call<IVue & IThisResolveSortingFields<TFilter> & { groupedQueryOptionsId: string }, [(queryOptions: ResolvedQueryOptions<TFilter>) => void], Promise<void>>(this, queryOptions => (this.queryOptions = queryOptions));

      const queryOptionsLoaded = new Events.QueryOptionsLoadedEvent({ id: this.$id, groupedQueryOptionsId: this.groupedQueryOptionsId });
      await WebApplicationConstants.listenerEventBuses.system.emit(queryOptionsLoaded);

      this.isQueryOptionsLoading = false;
    },
    methods: getMethods<TFilter>(),
  });
}
